import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { LiveTvChannel, LiveTvProviderSummary } from '../../api/types';
import { pluginKey } from '../../i18n';

/// A channel's identity across every plugin. Ids are only unique inside the plugin that issued
/// them, and the server compares them without regard to case, so this does too.
export function channelKey(providerId: string, channelId: string): string {
  return `${providerId.toLowerCase()}\n${channelId.toLowerCase()}`;
}

/// What a provider is called in the reader's language. A built-in plugin's key is the
/// application's own; an installed one ships the key in its catalogue, where it is namespaced
/// by the plugin id. Where neither has it, the id is the honest fallback.
export function useProviderName(providers: LiveTvProviderSummary[] | undefined) {
  const { t } = useTranslation();

  return useCallback(
    (providerId: string, displayNameKey?: string | null) => {
      const key = displayNameKey ?? providers?.find((provider) => provider.providerId === providerId)?.displayNameKey;

      return key ? t([key, pluginKey(providerId, key)], { defaultValue: providerId }) : providerId;
    },
    [providers, t],
  );
}

export type KindFilter = 'all' | 'Television' | 'Radio';
export type EncryptionFilter = 'all' | 'free' | 'encrypted';
export type VisibilityFilter = 'all' | 'visible' | 'hidden';

export interface ChannelFilter {
  providerId: string;
  kind: KindFilter;
  encryption: EncryptionFilter;
  search: string;
}

export const emptyChannelFilter: ChannelFilter = { providerId: 'all', kind: 'all', encryption: 'all', search: '' };

/// Whether a channel passes the filter. The search looks at the name, the number and the guide
/// id, because a person looking for "ARD" may be thinking of any of the three.
export function matchesFilter(channel: LiveTvChannel, filter: ChannelFilter, alsoSearch: (string | null)[] = []) {
  if (filter.providerId !== 'all' && channel.providerId !== filter.providerId) {
    return false;
  }

  if (filter.kind !== 'all' && channel.kind !== filter.kind) {
    return false;
  }

  if (filter.encryption === 'free' && channel.isEncrypted) {
    return false;
  }

  if (filter.encryption === 'encrypted' && !channel.isEncrypted) {
    return false;
  }

  const search = filter.search.trim().toLocaleLowerCase();

  if (!search) {
    return true;
  }

  return [channel.name, channel.number, channel.guideId, ...alsoSearch].some(
    (value) => value?.toLocaleLowerCase().includes(search) ?? false,
  );
}

/// By number the way a person reads numbers, then by name.
export function compareChannels(left: LiveTvChannel, right: LiveTvChannel): number {
  if (left.number && !right.number) {
    return -1;
  }

  if (!left.number && right.number) {
    return 1;
  }

  const byNumber = (left.number ?? '').localeCompare(right.number ?? '', undefined, { numeric: true });

  return byNumber !== 0 ? byNumber : left.name.localeCompare(right.name);
}
